import React, { useState, useEffect } from 'react';
import { useLanguage } from 'Context/LanguageContext';
import { getFailure } from 'FielderUtils/autoassign/autoassign';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import { Box } from '@mui/material';
import { TEMPLATE_STATUS } from '@core/constants/autoAssign/processTaskTemplate.constants';
import { ENDPOINTS } from '@core/constants/pageEndpoints.constants';
import AutoAssignTaskStatus from 'OldComponents/Status/AutoAssignTaskStatus';
import { DeleteIcon, TemplateRemovedText } from '../AutoAssignDetail.styles';
import { StatusContainer } from '~/Status/styled';
import { AliceBlue, CadetBlueCrayola } from '@core/theme/baseTheme';
import FielderButton from 'FielderComponents/FielderButton';
import { binarySearch } from '@core/utils/searchAlgorithms/binarySearch';
import TextDetail from '~/TextDetail/TextDetail.component';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';

export default function AutoassignInfo({ processTask, taskTypes }) {
  const lan = useLanguage();
  const user = useSelector(selectUser);
  const fielderMsg = useFielderSnackBar();
  const navigate = useNavigate();
  const [template, setTemplate] = useState('');
  const [templateStatus, setTemplateStatus] = useState(null);
  const [task, setTask] = useState('');
  const [status, setStatus] = useState('');
  const [taskId, setTaskId] = useState('');
  const [taskType, setTaskType] = useState('');
  const [origin, setOrigin] = useState('');
  const [customId, setCustomId] = useState('');
  const [priority, setPriority] = useState('');
  useEffect(() => {
    if (processTask) {
      const { template, task, status, taskId, templateStatus } = processTask;
      setTemplate(template);
      setTemplateStatus(templateStatus);
      setStatus(status);
      setTaskId(taskId);
      setTask(task);
      const { idCustom, serviceTypeId, branch, user: taskUser } = task || {};
      if (idCustom) setCustomId(idCustom);
      const tt = binarySearch(taskTypes, serviceTypeId);
      setTaskType(tt);
      if (branch) setOrigin(branch.name);
      else if (taskUser) setOrigin(`${taskUser.name} ${taskUser.lastName}`);
    }
  }, [processTask, taskTypes]);
  useEffect(() => {
    if (task?.severity) setPriority(lan[`severity_${task.severity}`]);
  }, [task, lan]);
  const getTime = () => {
    if (!template) return '';
    const sev = task?.severity;
    const sl = sev === 1 ? template.serviceLevel.high
      : sev === 2 ? template.serviceLevel.med
        : template.serviceLevel.low;
    if (sl.immediateResponse) return lan.immediateResponse;
    const hours = Math.floor(sl.time / 3600);
    const minutes = Math.floor((sl.time - hours * 3600) / 60);
    return `${hours} hrs ${minutes} min`;
  };
  const showTemplate = () => {
    if (RoleUtils.isUserManager(user)) {
      fielderMsg({
        title: lan.autoassign,
        message: lan.notAllowToSeeOrEdit,
        variant: 'warning',
        closeButton: true,
        duration: 7000,
      });
    } else {
      navigate(`${ENDPOINTS.autoAssign}/${template.id}`);
    }
  };
  const ViewTemplate = () => {
    if (templateStatus === TEMPLATE_STATUS.DELETED) {
      return (
        <StatusContainer color={CadetBlueCrayola} background={AliceBlue}>
          <Grid container alignItems="center" spacing={1}>
            <Grid size="auto">
              <DeleteIcon />
            </Grid>
            <Grid>
              <TemplateRemovedText>{lan.autoAssignTemplateDeleted}</TemplateRemovedText>
            </Grid>
          </Grid>
        </StatusContainer>
      );
    }
    return (
      <Grid container justifyContent="center" alignItems="center" spacing={1}>
        <Grid size={12}>
          <FielderButton outlined onClick={showTemplate} className="p-1">
            {lan.viewAutoassignTemplate}
          </FielderButton>
        </Grid>
        <Grid size="auto">
          {(templateStatus === TEMPLATE_STATUS.INACTIVE /* || true (was in original) */) && (
            <TemplateRemovedText>{lan.autoAssignTemplateIsInactive}</TemplateRemovedText>
          )}
        </Grid>
      </Grid>
    );
  };
  const LabelText = ({ label, text, sx }) => {
    const value = Array.isArray(text) ? text.join(' > ') : text;
    return (
      <Box sx={{ my: 1.5, ...sx }}>
        <TextDetail label={label} text={value} />
      </Box>
    );
  };
  return (
    <Grid container spacing={1}>
      {/* Left column */}
      <Grid size={6} container>
        <Grid size={12} container>
          <Grid size={7}>
            <LabelText label={lan.idTask} text={taskId}  />
          </Grid>
          <Grid size={4} sx={{ pt: 4 }}>
            {status && <AutoAssignTaskStatus value={status} />}
          </Grid>
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.idCustom} text={customId} />
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.folio} text={task?.folio ? task.folio : ''} />
        </Grid>
        <Grid size={12}>
          <LabelText label={`${lan.company}/${lan.client}`} text={origin} />
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.templateName} text={template ? template.name : ''} />
        </Grid>
      </Grid>
      {/* Right column */}
      <Grid size={6} container sx={{ pt: 2 }}>
        <Grid size={12}>
          <ViewTemplate />
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.typeTask} text={taskType ? taskType.name : ''} />
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.priority} text={priority} />
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.responseTime} text={getTime()} />
        </Grid>
        <Grid size={12}>
          <LabelText label={lan.failureReason} text={getFailure(status, lan)} />
        </Grid>
      </Grid>
    </Grid>
  );
}
